<template>
  <div class="tw:flex tw:flex-col tw:gap-4" data-testid="candidate-file-requests">
    <div v-if="isLoading" class="tw:flex tw:justify-center tw:py-3 tw:text-gray-500">
      <IconLoader2 :size="20" class="tw:animate-spin" :aria-label="$t('common.loading')" />
    </div>
    <ul v-else-if="requests.length" class="tw:m-0! tw:flex tw:list-none tw:flex-col tw:gap-2.5 tw:p-0!">
      <li
        v-for="fileRequest in visibleRequests"
        :key="fileRequest.id"
        class="tw:flex tw:flex-col tw:gap-1 tw:rounded-md tw:border tw:border-solid tw:border-gray-200 tw:p-3">
        <div class="tw:flex tw:items-center tw:gap-2">
          <span class="tw:text-13 tw:font-semibold tw:text-gray-900">{{ macroLabel(fileRequest.macro_key) }}</span>
          <LeadPill :tone="FILE_REQUEST_STATUS[fileRequestStatus(fileRequest)].tone">
            {{ $t(FILE_REQUEST_STATUS[fileRequestStatus(fileRequest)].labelKey) }}
          </LeadPill>
          <div class="tw:ml-auto tw:flex tw:items-center tw:gap-1">
            <LeadButton
              v-if="
                fileRequestStatus(fileRequest) === 'pending' &&
                resendPickerRequestId !== fileRequest.id &&
                props.availableMediums.length
              "
              variant="ghost"
              size="sm"
              :icon="IconRefresh"
              :disabled="cancellingId === fileRequest.id"
              :aria-label="$t('candidate.file_requests.resend_button')"
              v-tooltip="$t('candidate.file_requests.resend_button')"
              @click="openResendPicker(fileRequest)" />
            <LeadButton
              v-if="isCancellable(fileRequest)"
              variant="ghost"
              size="sm"
              :icon="IconX"
              :loading="cancellingId === fileRequest.id"
              :disabled="resendingId === fileRequest.id"
              :aria-label="$t('candidate.file_requests.cancel_button')"
              v-tooltip="$t('candidate.file_requests.cancel_button')"
              @click="cancel(fileRequest)" />
          </div>
        </div>

        <div
          v-if="fileRequestStatus(fileRequest) === 'pending' && resendPickerRequestId === fileRequest.id"
          class="tw:mt-1 tw:flex tw:flex-col tw:gap-3 tw:border-t tw:border-solid tw:border-gray-200 tw:pt-3">
          <LeadTabs v-model="resendPickerMedium" variant="boxed" :tabs="mediumTabs(props.availableMediums)" />
          <div class="tw:flex tw:justify-end tw:gap-2">
            <LeadButton
              variant="white"
              :disabled="resendingId === fileRequest.id"
              @click="resendPickerRequestId = null">
              {{ $t('candidate.file_requests.cancel_form') }}
            </LeadButton>
            <LeadButton
              variant="primary"
              :disabled="!props.availableMediums.length"
              :loading="resendingId === fileRequest.id"
              @click="confirmResend(fileRequest)">
              {{ $t('candidate.file_requests.resend_button') }}
            </LeadButton>
          </div>
        </div>

        <div v-if="fileRequest.custom_message" class="tw:whitespace-pre-wrap tw:text-13 tw:text-gray-600">
          {{ fileRequest.custom_message }}
        </div>
        <div class="tw:text-12 tw:text-gray-500">
          {{ $t('candidate.file_requests.created_at', { date: formatDateTime(fileRequest.created_at) }) }}
          · {{ fileRequest.medium.toUpperCase() }}
        </div>

        <ul
          v-if="fileRequest.uploads?.length"
          class="tw:mt-1.5! tw:mb-0! tw:flex tw:list-none tw:flex-col tw:gap-1 tw:p-0!">
          <li
            v-for="upload in fileRequest.uploads"
            :key="upload.file_sha256"
            class="tw:flex tw:items-center tw:gap-2 tw:text-13">
            <IconFileText :size="16" class="tw:shrink-0 tw:text-gray-500" />
            <span class="tw:flex tw:min-w-0 tw:flex-col">
              <span class="tw:truncate tw:font-medium tw:text-gray-900">
                {{ upload.file_name ?? $t('candidate.file_requests.unnamed_file') }}
              </span>
              <span class="tw:text-12 tw:text-gray-500">{{ formatDateTime(upload.uploaded_at) }}</span>
            </span>
            <LeadButton
              variant="white"
              size="sm"
              :icon="IconDownload"
              class="tw:ml-auto"
              :loading="downloadingFileSha256 === upload.file_sha256"
              @click="downloadUpload(fileRequest, upload)">
              {{ $t('candidate.detail.download_file') }}
            </LeadButton>
          </li>
        </ul>
      </li>
    </ul>
    <p v-else class="text-muted mb-0">{{ $t('candidate.file_requests.no_requests') }}</p>

    <LeadButton v-if="hasMore" variant="link" class="tw:self-start" @click="showAll = true">
      {{ $t('candidate.file_requests.show_more', { count: requests.length - INITIAL_VISIBLE }) }}
    </LeadButton>

    <CandidateMessagingNotice
      v-if="!props.orderFinished && !props.availableMediums.length"
      data-testid="candidate-no-contact">
      {{ $t('candidate.messaging.no_contact') }}
    </CandidateMessagingNotice>

    <div v-else-if="!props.orderFinished && !showForm">
      <LeadButton variant="white" :icon="IconFilePlus" data-testid="file-request-open-form" @click="showForm = true">
        {{ $t('candidate.file_requests.request_button') }}
      </LeadButton>
    </div>

    <div
      v-else-if="!props.orderFinished"
      class="tw:flex tw:flex-col tw:gap-3 tw:border-t tw:border-solid tw:border-gray-200 tw:pt-4">
      <LeadTabs v-model="form.medium" variant="boxed" :tabs="mediumTabs(props.availableMediums)" />

      <div>
        <LeadSelect
          :model-value="form.macro_key"
          :options="macroOptions"
          :filterable="false"
          @update:model-value="(value) => (form.macro_key = value as FileRequestMacroKey)" />
        <p v-if="hasPendingForMacro" class="tw:mt-1.5! tw:mb-0! tw:text-13 tw:text-gray-600">
          {{ $t('candidate.file_requests.pending_exists') }}
        </p>
      </div>

      <LeadTextarea
        v-if="form.macro_key === 'custom'"
        v-model="form.custom_message"
        :rows="3"
        :placeholder="$t('candidate.file_requests.custom_message_placeholder')" />

      <div class="tw:flex tw:justify-end tw:gap-2">
        <LeadButton variant="white" :disabled="isSubmitting" @click="resetForm">
          {{ $t('candidate.file_requests.cancel_form') }}
        </LeadButton>
        <LeadButton
          variant="primary"
          :loading="isSubmitting"
          :disabled="!canSubmit || hasPendingForMacro"
          @click="submit">
          {{ $t('candidate.file_requests.submit_button') }}
        </LeadButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { IconFileText, IconDownload, IconX, IconRefresh, IconFilePlus, IconLoader2 } from '@tabler/icons-vue';
import {
  fetchFileRequests,
  createFileRequest,
  resendFileRequest,
  deleteFileRequest,
  downloadFileRequestUpload,
} from '@/api/candidates';
import type {
  FileRequestMacroKey,
  FileRequestStatus,
  FormFileRequest,
  FormFileRequestUpload,
  FormMessageMedium,
} from '@/types/candidate';
import LeadButton from '@/components/lead-design/LeadButton.vue';
import LeadPill, { type LeadPillTone } from '@/components/lead-design/LeadPill.vue';
import LeadSelect, { type LeadSelectOption } from '@/components/lead-design/LeadSelect.vue';
import LeadTabs from '@/components/lead-design/LeadTabs.vue';
import LeadTextarea from '@/components/lead-design/LeadTextarea.vue';
import CandidateMessagingNotice from '@/components/candidate/CandidateMessagingNotice.vue';
import { useCandidateUpdates } from '@/composables/useCandidateUpdates';
import { availableMediumOrFirst, fileRequestStatus, mediumTabs } from '@/helpers/candidateMessaging';
import { downloadBlob } from '@/func/download';
import translator from '@/locale/translator';
import { formatDateTime } from '@/filters/dateFormat';

const INITIAL_VISIBLE = 3;

const FILE_REQUEST_STATUS: Record<FileRequestStatus, { labelKey: string; tone: LeadPillTone }> = {
  pending: { labelKey: 'candidate.file_requests.status_pending', tone: 'primary' },
  fulfilled: { labelKey: 'candidate.file_requests.status_fulfilled', tone: 'success' },
  cancelled: { labelKey: 'candidate.file_requests.status_cancelled', tone: 'danger' },
  expired: { labelKey: 'candidate.file_requests.status_expired', tone: 'neutral' },
};

const MACRO_LABEL_KEYS: Record<FileRequestMacroKey, string> = {
  cv_upload: 'candidate.file_requests.macro_cv',
  reference_upload: 'candidate.file_requests.macro_reference',
  custom: 'candidate.file_requests.macro_custom',
};

/* --------------------------------- PROPS ----------------------------------- */

interface Props {
  candidateEntityId: string;
  publicationId: number | null;
  orderId: number | null;
  orderFinished?: boolean;
  availableMediums: FormMessageMedium[];
}

const props = defineProps<Props>();

/* --------------------------------- STATE ----------------------------------- */

const toast = useToast();
const isLoading = ref(false);
const isSubmitting = ref(false);
const resendingId = ref<string | null>(null);
const cancellingId = ref<string | null>(null);
const downloadingFileSha256 = ref<string | null>(null);
const resendPickerRequestId = ref<string | null>(null);
const resendPickerMedium = ref<FormMessageMedium>('email');
const requests = ref<FormFileRequest[]>([]);
const showForm = ref(false);
const showAll = ref(false);

const form = ref<{
  medium: FormMessageMedium;
  macro_key: FileRequestMacroKey;
  custom_message: string;
}>({
  medium: 'email',
  macro_key: 'cv_upload',
  custom_message: '',
});

/* --------------------------------- COMPUTED -------------------------------- */

const canSubmit = computed(() => {
  if (props.publicationId === null || props.orderId === null) return false;
  if (!props.availableMediums.includes(form.value.medium)) return false;
  if (form.value.macro_key === 'custom' && !form.value.custom_message.trim()) return false;
  return true;
});

const hasPendingForMacro = computed(
  () =>
    form.value.macro_key !== 'custom' &&
    requests.value.some(
      (fileRequest) => fileRequest.macro_key === form.value.macro_key && fileRequestStatus(fileRequest) === 'pending'
    )
);

const visibleRequests = computed(() => (showAll.value ? requests.value : requests.value.slice(0, INITIAL_VISIBLE)));

const hasMore = computed(() => !showAll.value && requests.value.length > INITIAL_VISIBLE);

const macroOptions = computed<LeadSelectOption[]>(() =>
  (Object.keys(MACRO_LABEL_KEYS) as FileRequestMacroKey[]).map((macroKey) => ({
    value: macroKey,
    label: macroLabel(macroKey),
  }))
);

/* --------------------------------- METHODS --------------------------------- */

const macroLabel = (macroKey: FileRequestMacroKey): string => translator.get(MACRO_LABEL_KEYS[macroKey]);

// a fulfilled request holds the candidate's uploads, so it stays
const isCancellable = (fileRequest: FormFileRequest) =>
  ['pending', 'expired'].includes(fileRequestStatus(fileRequest)) && resendPickerRequestId.value !== fileRequest.id;

// failures below are toasted by the API client
const downloadUpload = async (fileRequest: FormFileRequest, upload: FormFileRequestUpload) => {
  downloadingFileSha256.value = upload.file_sha256;
  try {
    const blob = await downloadFileRequestUpload(props.candidateEntityId, fileRequest.id, upload.file_sha256);
    downloadBlob(blob, upload.file_name ?? translator.get('candidate.file_requests.unnamed_file'));
  } catch (error) {
    console.error('error downloading file request upload:', error);
  } finally {
    downloadingFileSha256.value = null;
  }
};

const loadRequests = async () => {
  isLoading.value = true;
  try {
    const fileRequests = await fetchFileRequests(props.candidateEntityId, props.orderId);
    requests.value = fileRequests.filter((fileRequest) => !fileRequest.cancelled_at);
  } catch (error) {
    console.error('error loading file requests:', error);
  } finally {
    isLoading.value = false;
  }
};

const resetForm = () => {
  showForm.value = false;
  form.value.medium = availableMediumOrFirst('email', props.availableMediums);
  form.value.macro_key = 'cv_upload';
  form.value.custom_message = '';
};

const submit = async () => {
  if (!canSubmit.value || props.publicationId === null || props.orderId === null) return;

  isSubmitting.value = true;
  try {
    const payload: Parameters<typeof createFileRequest>[1] = {
      medium: form.value.medium,
      macro_key: form.value.macro_key,
      publication_id: props.publicationId,
      order_id: props.orderId,
    };
    if (form.value.macro_key === 'custom') {
      payload.custom_message = form.value.custom_message.trim();
    }

    await createFileRequest(props.candidateEntityId, payload);
    resetForm();
    await loadRequests();
  } catch (error) {
    console.error('error creating file request:', error);
  } finally {
    isSubmitting.value = false;
  }
};

const cancel = async (fileRequest: FormFileRequest) => {
  cancellingId.value = fileRequest.id;
  try {
    await deleteFileRequest(props.candidateEntityId, fileRequest.id);
    await loadRequests();
  } catch (error) {
    console.error('error cancelling file request:', error);
  } finally {
    cancellingId.value = null;
  }
};

const openResendPicker = (fileRequest: FormFileRequest) => {
  resendPickerRequestId.value = fileRequest.id;
  resendPickerMedium.value = availableMediumOrFirst(fileRequest.medium, props.availableMediums);
};

const confirmResend = async (fileRequest: FormFileRequest) => {
  if (props.publicationId === null || props.orderId === null || !props.availableMediums.length) return;

  resendingId.value = fileRequest.id;
  try {
    if (resendPickerMedium.value === fileRequest.medium) {
      await resendFileRequest(props.candidateEntityId, fileRequest.id, fileRequest.medium);
    } else {
      // the medium is fixed per request, so another medium means replacing it
      await deleteFileRequest(props.candidateEntityId, fileRequest.id);
      await createFileRequest(props.candidateEntityId, {
        medium: resendPickerMedium.value,
        macro_key: fileRequest.macro_key,
        publication_id: props.publicationId,
        order_id: props.orderId,
        ...(fileRequest.custom_message ? { custom_message: fileRequest.custom_message } : {}),
      });
    }
    resendPickerRequestId.value = null;
    await loadRequests();
    toast.success(translator.get('candidate.file_requests.resend_success'));
  } catch (error) {
    console.error('error resending file request:', error);
  } finally {
    resendingId.value = null;
  }
};

/* --------------------------------- WATCHERS -------------------------------- */

// a channel the candidate cannot be reached on is not offered, so the form must not stay on it
watch(
  () => props.availableMediums,
  (availableMediums) => {
    form.value.medium = availableMediumOrFirst(form.value.medium, availableMediums);
  },
  { immediate: true }
);

// each order has its own file requests
watch(() => props.orderId, loadRequests);

/* ------------------------------ LIFECYCLE HOOKS ---------------------------- */

useCandidateUpdates(() => props.candidateEntityId, 'file_requests', loadRequests);

onMounted(loadRequests);

defineExpose({ refresh: loadRequests, isLoading });
</script>
